import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Grid,
    Typography,
    Paper,
    Divider,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Print as PrintIcon,
    Edit as EditIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { OrderInvoice } from './OrderInvoice';
import { getOrderById, updateOrderStatus, getOrderStatusLogs, assignOrder, reassignOrder } from '../../../shared/services/orderService';
import { refundPayment } from '../../../shared/services/paymentService';
import { userService } from '../../../shared/services/userService';
import { resolveImageUrl } from '../../../shared/utils/imageUrl';
import { ProductImage } from '../../../shared/components/ProductImage';
import { Order, OrderStatus, OrderPaymentStatus, OrderStatusLog } from '../../../shared/types/order';
import moment from 'moment';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';

export const OrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();

    const [order, setOrder] = useState<Order | null>(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');

    const [updateError, setUpdateError] = useState('');
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<OrderStatus>('PLACED');
    const [newPaymentStatus, setNewPaymentStatus] = useState<OrderPaymentStatus>('PENDING');

    const [statusLogs, setStatusLogs] = useState<OrderStatusLog[]>([]);
    const [statusLogsLoading, setStatusLogsLoading] = useState(false);

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
    const [deliveryBoysLoading, setDeliveryBoysLoading] = useState(false);
    const [selectedDeliveryBoyId, setSelectedDeliveryBoyId] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [deliveryBoySearchQuery, setDeliveryBoySearchQuery] = useState('');

    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [refundReason, setRefundReason] = useState('');
    const [refunding, setRefunding] = useState(false);
    const [refundSuccessMessage, setRefundSuccessMessage] = useState('');

    useEffect(() => {
        if (id) {
            fetchOrderDetails();
            fetchStatusLogs();
            fetchDeliveryBoys();
        }
    }, [id]);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const response = await getOrderById(id!);
            if (response.success) {
                setOrder(response.data);
                setNewStatus(response.data.orderStatus);
                setNewPaymentStatus(response.data.paymentStatus);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load order details');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        try {
            setUpdating(true);
            const response = await updateOrderStatus(id!, {
                orderStatus: newStatus,
                paymentStatus: newPaymentStatus
            });
            if (response.success) {
                const updatedOrder = response.data;
                setOrder(updatedOrder);
                setStatusDialogOpen(false);
                fetchStatusLogs();
                
                // If status is CONFIRMED and not assigned, open assign dialog
                if (newStatus === 'CONFIRMED' && !updatedOrder.assignedTo) {
                    handleOpenAssignDialog();
                }
            }
        } catch (err: any) {
            setUpdateError(err.response?.data?.message || 'Failed to update status');
        } finally {
            setUpdating(false);
        }
    };

    const needsRefundRetry = !!order &&
        order.orderStatus === 'CANCELLED' &&
        order.paymentMethod === 'ONLINE' &&
        order.paymentStatus === 'PAID' &&
        !!order.paymentId;

    const handleRetryRefund = async () => {
        if (!order?.paymentId) return;
        try {
            setRefunding(true);
            setUpdateError('');
            await refundPayment(order.paymentId, refundReason.trim() || undefined);
            setRefundDialogOpen(false);
            setRefundReason('');
            setRefundSuccessMessage('Refund processed successfully');
            await fetchOrderDetails();
            fetchStatusLogs();
        } catch (err: any) {
            setUpdateError(err.response?.data?.message || 'Failed to process refund');
        } finally {
            setRefunding(false);
        }
    };

    const fetchStatusLogs = async () => {
        try {
            setStatusLogsLoading(true);
            const response = await getOrderStatusLogs(id!);
            if (response.success) {
                setStatusLogs(response.data);
            }
        } catch {
            // silently ignore
        } finally {
            setStatusLogsLoading(false);
        }
    };

    const fetchDeliveryBoys = async () => {
        try {
            setDeliveryBoysLoading(true);
            const response = await userService.list({ limit: 1000 });
            const listData = response?.data?.items || response?.data || [];
            const allUsers = Array.isArray(listData) ? listData : [];
            const boys = allUsers.filter((u: any) =>
                u.usertype?.toLowerCase() === 'delivery' ||
                u.role?.toLowerCase() === 'delivery' ||
                u.type?.toLowerCase() === 'delivery'
            );
            setDeliveryBoys(boys);
        } catch (error) {
            console.error('Error fetching delivery boys:', error);
        } finally {
            setDeliveryBoysLoading(false);
        }
    };

    const getDeliveryBoyName = (assignedTo: any) => {
        if (!assignedTo) return 'N/A';
        if (typeof assignedTo === 'object') {
            return assignedTo.name || assignedTo.id || 'N/A';
        }
        
        const boy = deliveryBoys.find(b => (b._id === assignedTo || b.id === assignedTo));
        return boy ? boy.name : assignedTo;
    };

    const handleOpenAssignDialog = () => {
        setAssignDialogOpen(true);
        fetchDeliveryBoys();
        if (order?.assignedTo) {
            const currentId = typeof order.assignedTo === 'object' ? order.assignedTo.id : order.assignedTo;
            setSelectedDeliveryBoyId(currentId || '');
        } else {
            setSelectedDeliveryBoyId('');
        }
    };

    const handleAssignDelivery = async (deliveryBoyId: string, deliveryBoyName?: string) => {
        if (!deliveryBoyId) return;
        try {
            setAssigning(true);
            const isReassign = !!order?.assignedTo;
            const response = isReassign
                ? await reassignOrder(id!, deliveryBoyId)
                : await assignOrder(id!, deliveryBoyId);

            if (response.success) {
                const updatedOrder = response.data;
                
                // Enrich with name if backend only returns ID
                if (typeof updatedOrder.assignedTo === 'string' && deliveryBoyName) {
                    updatedOrder.assignedTo = { id: updatedOrder.assignedTo, name: deliveryBoyName };
                } else if (typeof updatedOrder.assignedTo === 'string') {
                    const boy = deliveryBoys.find(b => (b._id === updatedOrder.assignedTo || b.id === updatedOrder.assignedTo));
                    if (boy) {
                        updatedOrder.assignedTo = { id: boy._id || boy.id, name: boy.name, mobile: boy.mobile };
                    }
                }
                
                setOrder(updatedOrder);
                setAssignDialogOpen(false);
                fetchStatusLogs();
                setDeliveryBoySearchQuery('');
            }
        } catch (err: any) {
            setUpdateError(err.response?.data?.message || 'Failed to assign delivery');
        } finally {
            setAssigning(false);
        }
    };

    const filteredDeliveryBoys = deliveryBoys.filter(boy =>
        boy.name?.toLowerCase().includes(deliveryBoySearchQuery.toLowerCase()) ||
        boy.mobile?.toLowerCase().includes(deliveryBoySearchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !order) {
        return (
            <Box p={4}>
                <Alert severity="error">{error || 'Order not found'}</Alert>
                <AppButton variant="secondary" onClick={() => navigate('/orders')} sx={{ mt: 2 }}>
                    Back to Orders
                </AppButton>
            </Box>
        );
    }

    return (
        <div className="flex flex-col flex-auto min-w-0 p-6 sm:p-10 bg-gray-50">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/orders">
                        <IconButton sx={{ color: '#0EA5E9' }}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Link>
                    <div>
                        <Typography variant="h4" fontWeight="bold">
                            Order {order.orderNumber}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Placed on {moment(order.placedAt).format('MMMM Do YYYY, h:mm:ss a')}
                        </Typography>
                    </div>
                </div>
                <div className="flex gap-3">
                    <AppButton
                        variant="secondary"
                        startIcon={<PrintIcon />}
                        onClick={() => setInvoiceOpen(true)}
                    >
                        Print Invoice
                    </AppButton>
                    {(isSuperAdmin() || hasPermission('orders.update')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setStatusDialogOpen(true)}
                        >
                            Update Status
                        </AppButton>
                    )}
                </div>
            </div>

            {needsRefundRetry && (
                <Alert
                    severity="error"
                    sx={{ mb: 4 }}
                    action={
                        (isSuperAdmin() || hasPermission('orders.update')) && (
                            <AppButton
                                variant="danger"
                                size="small"
                                onClick={() => setRefundDialogOpen(true)}
                            >
                                Retry Refund
                            </AppButton>
                        )
                    }
                >
                    This order is cancelled but the payment is still marked PAID — the automatic refund
                    did not go through. Retry it manually below.
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Left Column: Items and Billing */}
                <Grid item xs={12} lg={8}>
                    <Paper className="p-6 mb-6 rounded-xl shadow-sm border border-gray-100">
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Order Items ({order.totalItems})
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="right">Price</TableCell>
                                        <TableCell align="right">Qty</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.items.map((item) => (
                                        <TableRow key={item.itemId}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <ProductImage 
                                                        imageUrl={item.imageUrl} 
                                                        name={item.name} 
                                                        size={40} 
                                                    />
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {item.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">₹{item.price?.toLocaleString() || '0'}</TableCell>
                                            <TableCell align="right">{item.quantity}</TableCell>
                                            <TableCell align="right" className="font-bold">
                                                ₹{item.lineTotal?.toLocaleString() || '0'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper className="p-6 rounded-xl shadow-sm border border-gray-100">
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Payment & Totals
                        </Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Box className="bg-blue-50 p-4 rounded-lg">
                                    <Typography variant="caption" color="primary" fontWeight="bold" display="block" gutterBottom>
                                        PAYMENT METHOD
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {order.paymentMethod}
                                    </Typography>
                                    <Box mt={1}>
                                        <Chip
                                            label={order.paymentStatus}
                                            size="small"
                                            color={order.paymentStatus === 'PAID' ? 'success' : 'warning'}
                                        />
                                    </Box>
                                    {order.paymentId && (
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            ID: {order.paymentId}
                                        </Typography>
                                    )}
                                    {order.paymentStatus === 'REFUNDED' && order.refundedAt && (
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                                            Refunded on {moment(order.refundedAt).format('lll')}
                                        </Typography>
                                    )}
                                </Box>
                                {order.notes && (
                                    <Box mt={3}>
                                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                            NOTES
                                        </Typography>
                                        <Typography variant="body2" className="italic text-gray-600">
                                            "{order.notes}"
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                    {/* Total Items */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Total Items</Typography>
                                        <Typography variant="body2" fontWeight="medium">{order.totalItems}</Typography>
                                    </Box>

                                    {/* Total MRP */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Total MRP</Typography>
                                        <Typography variant="body2" fontWeight="medium">₹{order.totals.totalMrp?.toLocaleString() || '0'}</Typography>
                                    </Box>

                                    {/* Discount on MRP */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="error">Discount on MRP</Typography>
                                        <Typography variant="body2" color="error" fontWeight="medium">
                                            − ₹{order.totals.mrpDiscount?.toLocaleString() || '0'}
                                        </Typography>
                                    </Box>

                                    {/* Discounted Price (Including Tax) */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Discounted Price (Including Tax)</Typography>
                                        <Typography variant="body2" fontWeight="medium">₹{order.totals.subtotal?.toLocaleString() || '0'}</Typography>
                                    </Box>

                                    {/* Coupon Discount */}
                                    {order.totals.couponDiscount > 0 && (
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body2" color="error">
                                                Coupon Discount {order.couponCode && `(${order.couponCode})`}
                                            </Typography>
                                            <Typography variant="body2" color="error" fontWeight="medium">
                                                − ₹{order.totals.couponDiscount?.toLocaleString() || '0'}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Shipping Charge */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Shipping Charge</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {order.totals.deliveryCharge === 0 ? 'Free' : `₹${order.totals.deliveryCharge?.toLocaleString() || '0'}`}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {/* Grand Total */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#0EA5E9' }}>Grand Total</Typography>
                                        <Typography variant="h6" fontWeight="extrabold" sx={{ color: '#0EA5E9' }}>
                                            ₹{order.totals.grandTotal?.toLocaleString() || '0'}
                                        </Typography>
                                    </Box>

                                    {/* Tax Breakup Info Box */}
                                    {order.taxBreakup && (
                                        <Box sx={{
                                            mt: 1,
                                            p: 1.5,
                                            bgcolor: '#f0f9ff',
                                            border: '1px solid #bae6fd',
                                            borderRadius: 2,
                                        }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
                                                <Typography variant="caption" fontWeight="bold" color="primary">
                                                    TAX BREAKUP (Included in Discounted Price)
                                                </Typography>
                                            </Box>
                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="caption" color="text.secondary">Total Tax</Typography>
                                                    <Typography variant="caption" fontWeight="bold">₹{(order.totals.tax || 0).toFixed(2)}</Typography>
                                                </Box>
                                                {Number(order.taxBreakup.cgst) !== 0 && (
                                                    <Box display="flex" justifyContent="space-between" sx={{ pl: 1.5, borderLeft: '2px solid #bae6fd' }}>
                                                        <Typography variant="caption" color="text.secondary">CGST</Typography>
                                                        <Typography variant="caption" color="text.secondary">₹{(order.taxBreakup?.cgst || 0).toFixed(2)}</Typography>
                                                    </Box>
                                                )}
                                                {Number(order.taxBreakup.sgst) !== 0 && (
                                                    <Box display="flex" justifyContent="space-between" sx={{ pl: 1.5, borderLeft: '2px solid #bae6fd' }}>
                                                        <Typography variant="caption" color="text.secondary">SGST</Typography>
                                                        <Typography variant="caption" color="text.secondary">₹{(order.taxBreakup?.sgst || 0).toFixed(2)}</Typography>
                                                    </Box>
                                                )}
                                                {Number(order.taxBreakup.igst) !== 0 && (
                                                    <Box display="flex" justifyContent="space-between" sx={{ pl: 1.5, borderLeft: '2px solid #bae6fd' }}>
                                                        <Typography variant="caption" color="text.secondary">IGST</Typography>
                                                        <Typography variant="caption" color="text.secondary">₹{(order.taxBreakup?.igst || 0).toFixed(2)}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Right Column: Customer and Shipping */}
                <Grid item xs={12} lg={4}>
                    <Paper className="p-6 mb-6 rounded-xl shadow-sm border border-gray-100">
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Status Overview
                        </Typography>
                        <Box mt={2}>
                            <Typography variant="caption" color="textSecondary">Order Status</Typography>
                            <Box mt={0.5} mb={2}>
                                <Chip
                                    label={order.orderStatus}
                                    sx={{ py: 2, px: 1, height: 'auto', fontWeight: 'bold' }}
                                    color={
                                        order.orderStatus === 'DELIVERED' ? 'success' :
                                            order.orderStatus === 'CANCELLED' ? 'error' :
                                                order.orderStatus === 'REFUND_INITIATED' ? 'warning' :
                                                    order.orderStatus === 'REFUNDED' ? 'info' :
                                                        ['ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY'].includes(order.orderStatus) ? 'secondary' : 'primary'
                                    }
                                />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {order.cancelledAt && (
                                <Box mb={2}>
                                    <Typography variant="caption" color="error" fontWeight="bold">CANCELLED AT</Typography>
                                    <Typography variant="body2">{moment(order.cancelledAt).format('lll')}</Typography>
                                    {order.cancelReason && (
                                        <Typography variant="body2" color="error" className="mt-1">
                                            Reason: {order.cancelReason}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Box>
                                <Typography variant="caption" color="textSecondary">Created</Typography>
                                <Typography variant="body2">{moment(order.createdAt).format('lll')}</Typography>
                            </Box>

                            {order.assignedTo ? (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" color="success.main" fontWeight="bold">DELIVERY ASSIGNED TO</Typography>
                                            {(isSuperAdmin() || hasPermission('orders.update')) && !['CANCELLED', 'REFUND_INITIATED', 'REFUNDED'].includes(order.orderStatus) && (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={handleOpenAssignDialog}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Typography variant="body1" fontWeight="bold">
                                            {getDeliveryBoyName(order.assignedTo)}
                                        </Typography>
                                        {order.assignedAt && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                Assigned on {moment(order.assignedAt).format('lll')}
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                (isSuperAdmin() || hasPermission('orders.update')) && !['CANCELLED', 'REFUND_INITIATED', 'REFUNDED'].includes(order.orderStatus) && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <AppButton
                                            variant="secondary"
                                            startIcon={<PersonAddIcon />}
                                            fullWidth
                                            onClick={handleOpenAssignDialog}
                                        >
                                            Assign Delivery
                                        </AppButton>
                                    </>
                                )
                            )}
                        </Box>
                    </Paper>

                    <Paper className="p-6 rounded-xl shadow-sm border border-gray-100">
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Delivery Address
                        </Typography>
                        {order.address ? (
                            <Box mt={2}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {order.address.receiverName}
                                </Typography>
                                <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
                                    {order.address.receiverPhone}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    {order.address.houseNoAndStreetName}
                                </Typography>
                                {order.address.landmark && (
                                    <Typography variant="body2" color="textSecondary">
                                        Landmark: {order.address.landmark}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="textSecondary">
                                    {order.address.city}, {order.address.state} - {order.address.pincode}
                                </Typography>
                                <Box mt={2}>
                                    <Chip label={order.address.type.toUpperCase()} size="small" variant="filled" />
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Store Pickup / No address info
                            </Typography>
                        )}
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                            CUSTOMER ID
                        </Typography>
                        <Typography variant="caption" display="block" className="truncate text-gray-400">
                            {order.jwtUserId}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Status Logs Timeline */}
            <Paper className="p-6 mt-6 rounded-xl shadow-sm border border-gray-100">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Status History
                </Typography>
                {statusLogsLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : statusLogs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No status logs found.</Typography>
                ) : (
                    <Box sx={{ position: 'relative', pl: 3, mt: 2 }}>
                        {/* vertical connector line */}
                        <Box sx={{
                            position: 'absolute',
                            left: 11,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            bgcolor: '#e0f2fe',
                            borderRadius: 1,
                        }} />
                        {statusLogs.map((log, index) => {
                            const isFirst = index === 0;
                            const dotColor =
                                log.newStatus === 'DELIVERED' ? '#22c55e' :
                                log.newStatus === 'CANCELLED' ? '#ef4444' :
                                log.newStatus === 'REFUND_INITIATED' ? '#f59e0b' :
                                log.newStatus === 'REFUNDED' ? '#0EA5E9' :
                                log.newStatus === 'OUT_FOR_DELIVERY' ? '#8b5cf6' :
                                '#0EA5E9';
                            return (
                                <Box key={log._id} sx={{ display: 'flex', gap: 2.5, mb: index < statusLogs.length - 1 ? 3.5 : 0 }}>
                                    <Box sx={{
                                        flexShrink: 0,
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        bgcolor: isFirst ? dotColor : '#fff',
                                        border: `3px solid ${dotColor}`,
                                        zIndex: 1,
                                        mt: 0.4,
                                    }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                            {log.oldStatus !== log.newStatus && (
                                                <>
                                                    <Chip
                                                        label={log.oldStatus}
                                                        size="small"
                                                        sx={{ fontWeight: 'bold', fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b' }}
                                                    />
                                                    <Typography variant="caption" color="textSecondary">→</Typography>
                                                </>
                                            )}
                                            <Chip
                                                label={log.newStatus}
                                                size="small"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '0.7rem',
                                                    bgcolor: dotColor + '1a',
                                                    color: dotColor,
                                                    border: `1px solid ${dotColor}40`,
                                                }}
                                            />
                                        </Box>
                                        {log.note && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {log.note}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                            {moment(log.createdAt).format('MMM Do YYYY, h:mm a')}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            {/* Update Status Dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Update Order Status</DialogTitle>
                <DialogContent>
                    <Box pt={2} className="space-y-4">
                        <TextField
                            select
                            label="Order Status"
                            fullWidth
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
                        >
                            <MenuItem value="PLACED">PLACED</MenuItem>
                            <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                            <MenuItem value="PICKED_UP">PICKED_UP</MenuItem>
                            <MenuItem value="OUT_FOR_DELIVERY">OUT_FOR_DELIVERY</MenuItem>
                            <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                            <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Payment Status"
                            fullWidth
                            value={newPaymentStatus}
                            onChange={(e) => setNewPaymentStatus(e.target.value as OrderPaymentStatus)}
                        >
                            <MenuItem value="PENDING">PENDING</MenuItem>
                            <MenuItem value="PAID">PAID</MenuItem>
                            <MenuItem value="FAILED">FAILED</MenuItem>
                            <MenuItem value="REFUNDED">REFUNDED</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <AppButton variant="secondary" onClick={() => setStatusDialogOpen(false)}>Cancel</AppButton>
                    <AppButton
                        variant="primary"
                        onClick={handleUpdateStatus}
                        disabled={updating}
                        loading={updating}
                    >
                        Update
                    </AppButton>
                </DialogActions>
            </Dialog>

            {/* Assign Delivery Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{order.assignedTo ? 'Reassign Delivery' : 'Assign Delivery'}</DialogTitle>
                <DialogContent>
                    <Box pt={1}>
                        <TextField
                            fullWidth
                            placeholder="Search delivery boy..."
                            variant="outlined"
                            size="small"
                            value={deliveryBoySearchQuery}
                            onChange={(e) => setDeliveryBoySearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />
                        {deliveryBoysLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
                                {filteredDeliveryBoys.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" align="center" py={2}>
                                        No delivery personnel found
                                    </Typography>
                                ) : (
                                    filteredDeliveryBoys.map((boy) => (
                                        <ListItem disablePadding key={boy._id || boy.id}>
                                            <ListItemButton
                                                disabled={assigning}
                                                selected={selectedDeliveryBoyId === (boy._id || boy.id)}
                                                onClick={() => {
                                                    const boyId = boy._id || boy.id;
                                                    setSelectedDeliveryBoyId(boyId);
                                                    handleAssignDelivery(boyId, boy.name);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: 'primary.light',
                                                        '&:hover': { bgcolor: 'primary.light' }
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: 'grey.200', color: 'grey.600', fontSize: '1rem' }}>
                                                        {boy.name?.charAt(0).toLowerCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={boy.name}
                                                    secondary={boy.mobile}
                                                    primaryTypographyProps={{ fontWeight: 'medium' }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <AppButton
                        variant="secondary"
                        onClick={() => setAssignDialogOpen(false)}
                        sx={{ fontWeight: 'bold' }}
                    >
                        CANCEL
                    </AppButton>
                </DialogActions>
            </Dialog>

            {/* Retry Refund Dialog */}
            <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Retry Refund</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This will attempt the Razorpay refund again for this payment. The customer's card/UPI
                        account will be credited if it succeeds.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Reason (optional)"
                        multiline
                        rows={2}
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton variant="secondary" onClick={() => setRefundDialogOpen(false)} disabled={refunding}>
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="danger"
                        onClick={handleRetryRefund}
                        disabled={refunding}
                        loading={refunding}
                    >
                        Retry Refund
                    </AppButton>
                </DialogActions>
            </Dialog>

            {invoiceOpen && (
                <OrderInvoice
                    open={invoiceOpen}
                    onClose={() => setInvoiceOpen(false)}
                    order={order}
                />
            )}

            <Snackbar
                open={!!updateError}
                autoHideDuration={4000}
                onClose={() => setUpdateError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setUpdateError('')} severity="error" sx={{ width: '100%' }}>
                    {updateError}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!refundSuccessMessage}
                autoHideDuration={4000}
                onClose={() => setRefundSuccessMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setRefundSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
                    {refundSuccessMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};
